import type { ItemState } from "@features/session/hooks/types";
import type { BaseItemSchema } from "@memorang/types/src/session/new-session";

export type ProethicaTestScore = {
	numCorrect: number;
	numTotal: number;
	percentage: number;
};

// Post-test pass gate: scoring >= 80% unlocks review and persists the pass.
export const PROETHICA_PASS_PERCENTAGE = 80;

export const isProethicaTestPassed = (percentage: number): boolean =>
	percentage >= PROETHICA_PASS_PERCENTAGE;

const isItemCorrect = (
	item: BaseItemSchema,
	selectedChoiceIds: string[],
): boolean => {
	const correctIds = (item.content?.choices ?? [])
		.filter((choice) => choice.isCorrect)
		.map((choice) => choice.id);

	if (!(correctIds.length && selectedChoiceIds.length)) {
		return false;
	}

	return (
		correctIds.length === selectedChoiceIds.length &&
		correctIds.every((id) => selectedChoiceIds.includes(id))
	);
};

// Pre/post tests are scored from local session state: each item's selection in
// `itemStates` is matched against the choices flagged correct. Unanswered items
// count as incorrect, so the total always reflects every question.
export const computeProethicaTestScore = (
	sessionItems: BaseItemSchema[],
	itemStates?: Map<number, ItemState>,
): ProethicaTestScore => {
	const numTotal = sessionItems.length;
	const numCorrect = sessionItems.reduce((count, item) => {
		const selectedChoiceIds =
			item.id != null
				? (itemStates?.get(item.id)?.selectedChoiceIds ?? [])
				: [];
		return isItemCorrect(item, selectedChoiceIds) ? count + 1 : count;
	}, 0);

	return {
		numCorrect,
		numTotal,
		percentage: numTotal ? Math.round((numCorrect / numTotal) * 100) : 0,
	};
};
